import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { track } from './tracking/tracker';
import { emitAddToCart, emitCartValue } from './layers/bus';

/**
 * Kosik zije v prohlizeci.
 *
 * Drzi si nazev a cenu jen kvuli zobrazeni – ZAVAZNA je vzdy cena ze
 * serveru. Kdyz se mezitim cenik zmeni, zakaznik zaplati aktualni cenu
 * a kosik se srovna, jakmile si vyzada souhrn dopravy.
 */
export interface CartLine {
  readonly variantId: string;
  readonly quantity: number;
  /** Jen pro zobrazeni. */
  readonly productName: string;
  readonly variantName: string;
  readonly slug: string;
  readonly priceMinor: number;
  readonly available: number;
  /**
   * MIN/MAX OBJEDNATELNÉ MNOŽSTVÍ (#23) – jen pro pohodlí ovládání.
   *
   * ZÁVAZNÉ JE MIN/MAX ZE SERVERU: pokladna (`shop/checkout.ts`) si limit
   * načte z databáze a objednávku mimo rozsah odmítne, ať už si tenhle
   * košík v localStorage kdokoli přepíše na cokoli. Tady je jen proto, aby
   * plusko/mínusko a políčko množství samo od sebe nenabízela množství,
   * které pokladna nevezme.
   *
   * VOLITELNÉ ZÁMĚRNĚ: v localStorage leží košíky založené před #23 a
   * dlaždice katalogu (`storefront/ProductCard.tsx`) limity nezná. Chybějící
   * hodnota znamená „bez omezení" – tedy přesně dnešní chování.
   */
  readonly minQuantity?: number;
  readonly maxQuantity?: number | null;
}

interface CartState {
  readonly lines: readonly CartLine[];
  readonly count: number;
  /** Orientacni soucet z ulozenych cen. Zavazny je az soucet ze serveru. */
  readonly estimatedTotalMinor: number;
  add(line: Omit<CartLine, 'quantity'>, quantity: number): void;
  setQuantity(variantId: string, quantity: number): void;
  remove(variantId: string): void;
  clear(): void;
}

const STORAGE_KEY = 'zooshopik.cart.v1';

const CartContext = createContext<CartState | null>(null);

function readStorage(): CartLine[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Data z localStorage muze upravit kdokoli – bereme jen to, co dava smysl.
    return parsed.filter(
      (line): line is CartLine =>
        typeof line === 'object' &&
        line !== null &&
        typeof (line as CartLine).variantId === 'string' &&
        Number.isInteger((line as CartLine).quantity) &&
        (line as CartLine).quantity > 0,
    );
  } catch {
    // Privatni rezim nebo zakazane uloziste – kosik proste zacne prazdny.
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<readonly CartLine[]>(readStorage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
    } catch {
      // Kosik pak prezije jen do zavreni zalozky. Lepsi nez spadnout.
    }
  }, [lines]);

  // Triggery vrstev (cart_value) potrebuji zit hodnotu kosiku – sbernice je
  // ZAMERNE mimo track(): bez souhlasu tracker eventy zahazuje, ale trigger
  // musi fungovat dal (zobrazeni vrstvy neni tracking).
  useEffect(() => {
    emitCartValue(lines.reduce((sum, line) => sum + line.priceMinor * line.quantity, 0));
  }, [lines]);

  const add = useCallback((line: Omit<CartLine, 'quantity'>, quantity: number) => {
    setLines((current) => {
      const existing = current.find((item) => item.variantId === line.variantId);
      if (existing === undefined) {
        return [...current, { ...line, quantity: clamp(quantity, line) }];
      }
      return current.map((item) =>
        item.variantId === line.variantId
          ? { ...item, ...line, quantity: clamp(item.quantity + quantity, line) }
          : item,
      );
    });
    // CDP: jediny instrumentacni bod pokryva oba volajici (ProductPage
    // i ProductCard). Ceny jsou jen prezentacni – revenue pravda je na serveru.
    track('cart_item_added', {
      variant_id: line.variantId,
      product_name: line.productName,
      variant_name: line.variantName,
      quantity,
      price_minor: line.priceMinor,
    });
    // Hranovy trigger add_to_cart (mimo track, viz vyse).
    emitAddToCart();
  }, []);

  const setQuantity = useCallback((variantId: string, quantity: number) => {
    setLines((current) =>
      quantity <= 0
        ? current.filter((item) => item.variantId !== variantId)
        : current.map((item) =>
            item.variantId === variantId ? { ...item, quantity: clamp(quantity, item) } : item,
          ),
    );
    // Snizeni na nulu je odebrani – z pohledu chovani totez co `remove`.
    if (quantity <= 0) track('cart_item_removed', { variant_id: variantId });
  }, []);

  const remove = useCallback((variantId: string) => {
    setLines((current) => current.filter((item) => item.variantId !== variantId));
    track('cart_item_removed', { variant_id: variantId });
  }, []);

  const clear = useCallback(() => setLines([]), []);

  const value = useMemo<CartState>(
    () => ({
      lines,
      count: lines.reduce((sum, line) => sum + line.quantity, 0),
      estimatedTotalMinor: lines.reduce((sum, line) => sum + line.priceMinor * line.quantity, 0),
      add,
      setQuantity,
      remove,
      clear,
    }),
    [lines, add, setQuantity, remove, clear],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const context = useContext(CartContext);
  if (context === null) throw new Error('useCart musí být uvnitř <CartProvider>.');
  return context;
}

/**
 * Nikdy nedovolime dat do kosiku vic, nez je skladem – a nic mimo min/max
 * objednatelne mnozstvi (#23), kdyz je varianta ma.
 *
 * PORADI JE PODSTATNE: strop dopocitame jako minimum ze SKLADU a z MAXIMA
 * (co neni skladem, to se objednat neda ani kdyz to limit dovoluje), a teprve
 * do nej vejdeme mnozstvi. MINIMUM ale sklad NEPRETLACI: kdyz je skladem 3
 * kusy a objednava se po 6, spravna odpoved je „ted to nejde", ne „dej tam 6
 * kusu, ktere nemame". Takovy kosik proto zustane na dostupnem mnozstvi a
 * pokladna ho odmitne srozumitelnou hlaskou – misto aby vyloha tise slozila
 * kosik, ktery neprojde ani skladem, ani limitem.
 *
 * TOHLE NENI OCHRANA, JE TO POHODLI. Zavazne min/max hlida server; localStorage
 * si prepise kdokoli.
 */
function clamp(
  quantity: number,
  bounds: { readonly available: number; readonly minQuantity?: number; readonly maxQuantity?: number | null },
): number {
  const stockCeiling = Math.max(1, bounds.available);
  const maxQuantity = bounds.maxQuantity ?? null;
  const ceiling = maxQuantity === null ? stockCeiling : Math.min(stockCeiling, maxQuantity);
  // Podlaha nesmi prelezt strop – jinak by se kosik naplnil tim, co neni.
  const floor = Math.min(Math.max(1, bounds.minQuantity ?? 1), ceiling);
  return Math.max(floor, Math.min(quantity, ceiling));
}
